import React, { useState, useEffect, useRef } from 'react';
import { IonContent, IonPage, IonButton, IonText, IonItem, IonLabel, IonModal } from '@ionic/react';
import { Helmet } from "react-helmet";

// Helper function to generate random letters
const generateRandomLetter = (length: number): string[] => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const randomLetters: string[] = [];
    for (let i = 0; i < length; i++) {
        const randomChar = letters.charAt(Math.floor(Math.random() * letters.length));
        randomLetters.push(randomChar);
    }
    return randomLetters;
};

// List of available dynamic patterns (Shapes, Colors, Letters)
const patterns = {
    shapes: ['⭐', '🌀', '◼', '🟡', '🔶', '🔺'], // Random set of shapes
    colors: ['🟢', '🟡', '🔴', '🔵', '🟣', '🟤'], // Random set of colors
    letters: generateRandomLetter(10), // Random set of 10 letters
} as const;

// Extract the keys of the patterns object as a type
type PatternType = keyof typeof patterns;

const Pattern: React.FC = () => {
    const [selectedPattern, setSelectedPattern] = useState<PatternType>('shapes');
    const [choosedPattern, setChoosedPattern] = useState<string>('');
    const [patternSequence, setPatternSequence] = useState<string>(''); // Change to string
    const [userChoice, setUserChoice] = useState<string>('');
    const [correctChoice, setCorrectChoice] = useState<string>('');
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [options, setOptions] = useState<string[]>([]);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const modal = useRef<HTMLIonModalElement>(null);

    // Randomly select a pattern and generate the question
    const generateRandomPatternQuestion = () => {
        const patternKeys: PatternType[] = ['shapes', 'colors', 'letters'];
        const randomPattern = patternKeys[Math.floor(Math.random() * 3)];
        const pattern = shuffleArray(patterns[randomPattern]);

        let sequence = "";
        let nextItem = "";

        const repetitionType = Math.floor(Math.random() * 5); // 0 to 7 for 8 patterns

        switch (repetitionType) {
            case 0:
                sequence = `${pattern[0]} ${pattern[1]} ${pattern[0]} ${pattern[1]} ${pattern[0]} ${pattern[1]} ${pattern[0]}`;
                nextItem = pattern[1];
                break;
            case 1:
                sequence = `${pattern[0]} ${pattern[1]} ${pattern[2]} ${pattern[3]} ${pattern[0]} ${pattern[1]} ${pattern[2]}`;
                nextItem = pattern[3];
                break;
            case 2:
                sequence = `${pattern[0]} ${pattern[0]} ${pattern[1]} ${pattern[1]} ${pattern[2]} ${pattern[2]} ${pattern[0]}`;
                nextItem = pattern[0];
                break;
            case 3:
                sequence = `${pattern[0]} ${pattern[1]} ${pattern[2]} ${pattern[0]} ${pattern[1]} ${pattern[2]} ${pattern[0]}`;
                nextItem = pattern[1];
                break;
            case 4:
                sequence = `${pattern[0]} ${pattern[1]} ${pattern[1]} ${pattern[1]} ${pattern[0]} ${pattern[1]} ${pattern[1]}`;
                nextItem = pattern[1];
                break;
            default:
                sequence = `${pattern[0]} ${pattern[1]}`;
                nextItem = pattern[0];
        }

        // Generate the incorrect options
        const incorrectOptions = generateIncorrectOptions(randomPattern, nextItem);

        // Set the sequence, correct answer, and options
        setChoosedPattern(randomPattern)
        setSelectedPattern(randomPattern);
        setPatternSequence(sequence); // Keep as a string
        setCorrectChoice(nextItem);
        setUserChoice('');
        setIsCorrect(null);
        setOptions(shuffleArray([nextItem, ...incorrectOptions]));
    };


    const shuffleArray = (array: any) => {
        return [...array].sort(() => Math.random() - 0.5);
    };

    // Generate 2 incorrect options
    const generateIncorrectOptions = (patternType: PatternType, correctAnswer: string): string[] => {

        const allOptions = [...patterns[patternType]];
        const filteredOptions = allOptions.filter((option) => option !== correctAnswer);
        const randomOptions: string[] = [];

        while (randomOptions.length < 2) {
            const randomIndex = Math.floor(Math.random() * filteredOptions.length);
            const option = filteredOptions[randomIndex];
            if (!randomOptions.includes(option)) {
                randomOptions.push(option);
            }
        }
        return randomOptions;
    };

    // Function to handle user selection and check correctness
    const handleAnswerSubmit = (selected: string) => {
        setUserChoice(selected);
        setIsCorrect(selected === correctChoice);


        if (selected === correctChoice) {
            setShowImage(true);
            setTimeout(() => {
                generateRandomPatternQuestion();
                setShowImage(false);
                setError(false);
            }, 5000); // Generate a new question after a correct answer
        }
        else {
            setError(true);
        }
    };

    // Generate a random pattern question when the component mounts
    useEffect(() => {
        generateRandomPatternQuestion();
        document.title = "Pattern Recognition Worksheet for LKG | Early Math Logic | Mama’s Guidance"
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce LKG children to patterns with engaging activities on Mama’s Guidance—designed to develop early math and logical thinking skills through colorful, interactive pattern recognition exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className='contentPart showCenter additionWord'>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Pattern Recognition</h1>

                                    <p>
                                        Recognizing and continuing <strong>patterns</strong> is a key math skill for young learners. This LKG worksheet introduces children to simple <strong>AB, ABC, and AAB patterns</strong> using colors, shapes, objects, and pictures. Through these playful activities, kids learn how to observe, predict, and build logical thinking.
                                    </p>

                                    <h2>🔁 What Are Patterns?</h2>
                                    <p>
                                        Patterns are things that repeat in a sequence, such as <strong>red-blue-red-blue</strong> or <strong>circle-square-triangle</strong>. Identifying patterns helps children understand how things follow a rule or order.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify simple repeating patterns (<strong>AB, ABB, ABC</strong>)</li>
                                        <li>Predict what comes next in a sequence</li>
                                        <li>Strengthen visual memory and attention</li>
                                        <li>Develop early problem-solving and logic skills</li>
                                    </ul>

                                    <h2>📋 What’s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Color patterns (<strong>e.g., red-green-red-green</strong>)</li>
                                        <li>Shape patterns (<strong>circle-square-circle-?</strong>)</li>
                                        <li>Object patterns using real-world items (<strong>ball, bat, ball, bat…</strong>)</li>
                                        <li>Fill in the blank to complete the sequence</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                    <ul>
                                        <li>Use beads, crayons, or toys to make real-life patterns</li>
                                        <li>Ask your child to find patterns around the house (tiles, clothes, curtains)</li>
                                        <li>Clap or tap in patterns and let them repeat</li>
                                        <li>Draw patterns on paper and leave blanks to complete</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Turn everything into a pattern game! <strong>“Spoon-fork-spoon-fork—what comes next?”</strong> Simple repetition helps make learning memorable and fun.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Pattern Recognition</h1>

                                <p>
                                    Recognizing and continuing <strong>patterns</strong> is a key math skill for young learners. This LKG worksheet introduces children to simple <strong>AB, ABC, and AAB patterns</strong> using colors, shapes, objects, and pictures. Through these playful activities, kids learn how to observe, predict, and build logical thinking.
                                </p>

                                <h2>🔁 What Are Patterns?</h2>
                                <p>
                                    Patterns are things that repeat in a sequence, such as <strong>red-blue-red-blue</strong> or <strong>circle-square-triangle</strong>. Identifying patterns helps children understand how things follow a rule or order.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify simple repeating patterns (<strong>AB, ABB, ABC</strong>)</li>
                                    <li>Predict what comes next in a sequence</li>
                                    <li>Strengthen visual memory and attention</li>
                                    <li>Develop early problem-solving and logic skills</li>
                                </ul>

                                <h2>📋 What’s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Color patterns (<strong>e.g., red-green-red-green</strong>)</li>
                                    <li>Shape patterns (<strong>circle-square-circle-?</strong>)</li>
                                    <li>Object patterns using real-world items (<strong>ball, bat, ball, bat…</strong>)</li>
                                    <li>Fill in the blank to complete the sequence</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Use beads, crayons, or toys to make real-life patterns</li>
                                    <li>Ask your child to find patterns around the house (tiles, clothes, curtains)</li>
                                    <li>Clap or tap in patterns and let them repeat</li>
                                    <li>Draw patterns on paper and leave blanks to complete</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/skip-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Turn everything into a pattern game! <strong>“Spoon-fork-spoon-fork—what comes next?”</strong> Simple repetition helps make learning memorable and fun.
                                </p>
                            </article>
                            <div className="ion-padding">
                                {/* Displaying the Current Sequence */}
                                <div style={{ fontSize: '2em', marginTop: '20px' }}>
                                    {patternSequence}
                                </div>

                                {/* Buttons for user's choices */}
                                <div style={{ marginTop: '20px' }}>
                                    <IonText>What comes next in the pattern?</IonText>
                                    <div className='optionDiv' style={{ marginTop: '20px', fontSize: "30px" }}>
                                        {options.map((option, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleAnswerSubmit(option)}
                                                style={{ margin: '5px' }}
                                                className={` ${userChoice === option
                                                    ? option === correctChoice
                                                        ? 'correct'
                                                        : 'incorrect'
                                                    : ''
                                                    }`}
                                            >
                                                {option}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default Pattern;
